import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Tag, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getBlogArticles } from '../data/blogData';
import { useLanguage } from '../contexts/LanguageContext';

// Helper function to get locale for date formatting
const getDateLocale = (language) => {
  const localeMap = {
    'en': 'en-GB',
    'nl': 'nl-BE',
    'fr': 'fr-FR'
  };
  return localeMap[language] || 'fr-FR';
};

const BlogArticle = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const articles = getBlogArticles(language);
  const article = articles.find(a => a.slug === slug);

  const articleTexts = {
    fr: {
      backBlog: 'Retour au blog',
      by: 'Par',
      reading: 'de lecture',
      share: 'Partager',
      tags: 'TAGS',
      ctaTitle: 'Besoin d\'une médiation ?',
      ctaText: 'Contactez-nous pour discuter de votre situation et découvrir comment la médiation peut aider votre entreprise.',
      ctaButton: 'Nous contacter',
      backButton: 'Retour au blog'
    },
    en: {
      backBlog: 'Back to blog',
      by: 'By',
      reading: 'read',
      share: 'Share',
      tags: 'TAGS',
      ctaTitle: 'Need mediation?',
      ctaText: 'Contact us to discuss your situation and discover how mediation can help your business.',
      ctaButton: 'Contact us',
      backButton: 'Back to blog'
    },
    nl: {
      backBlog: 'Terug naar blog',
      by: 'Door',
      reading: 'leestijd',
      share: 'Delen',
      tags: 'TAGS',
      ctaTitle: 'Bemiddeling nodig?',
      ctaText: 'Neem contact met ons op om uw situatie te bespreken en te ontdekken hoe bemiddeling uw bedrijf kan helpen.',
      ctaButton: 'Contacteer ons',
      backButton: 'Terug naar blog'
    }
  };

  const texts = articleTexts[language] || articleTexts.fr;

  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.excerpt,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      const messages = {
        fr: 'Lien copié dans le presse-papiers !',
        en: 'Link copied to clipboard!',
        nl: 'Link gekopieerd naar klembord!'
      };
      alert(messages[language] || messages.fr);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1e5f8c] to-[#2c7db5] text-white py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <Link to="/blog" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {texts.backBlog}
          </Link>
        </div>
      </div>

      {/* Article Content */}
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <article className="bg-white rounded-lg shadow-lg p-8 md:p-12">
          {/* Title */}
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            {article.title}
          </h1>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-6 mb-8 pb-8 border-b border-gray-200">
            <span className="flex items-center gap-2 text-gray-600">
              <Calendar className="w-5 h-5" />
              {new Date(article.date).toLocaleDateString(getDateLocale(language), {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </span>
            <span className="flex items-center gap-2 text-gray-600">
              <Clock className="w-5 h-5" />
              {article.readingTime} {texts.reading}
            </span>
            <span className="text-gray-600">
              {texts.by} {article.author}
            </span>
            <button
              onClick={handleShare}
              className="ml-auto flex items-center gap-2 text-[#1e5f8c] hover:text-[#164a6d] transition-colors"
            >
              <Share2 className="w-5 h-5" />
              {texts.share}
            </button>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            {article.content.split('\n\n').map((paragraph, index) => (
              <p key={`${article.id}-paragraph-${index}-${paragraph.substring(0, 20)}`} className="mb-6 text-gray-700 leading-relaxed text-lg">
                {paragraph}
              </p>
            ))}
          </div>

          {/* Tags */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-600 mb-4">{texts.tags}</h3>
            <div className="flex flex-wrap gap-3">
              {article.tags.map((tag) => (
                <span key={tag} className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-medium">
                  <Tag className="w-4 h-4" />
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 p-8 bg-gradient-to-r from-[#1e5f8c] to-[#2c7db5] rounded-lg text-white">
            <h3 className="text-2xl font-bold mb-4">{texts.ctaTitle}</h3>
            <p className="text-white/90 mb-6">
              {texts.ctaText}
            </p>
            <Link to="/#contact">
              <Button className="bg-white text-[#1e5f8c] hover:bg-gray-100 font-semibold">
                {texts.ctaButton}
              </Button>
            </Link>
          </div>
        </article>

        {/* Back to Blog */}
        <div className="mt-12 text-center">
          <Link to="/blog">
            <Button variant="outline" className="border-[#1e5f8c] text-[#1e5f8c] hover:bg-[#1e5f8c] hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {texts.backButton}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BlogArticle;
